import React from 'react';
import PropTypes from 'prop-types';
import NextLink from 'next/link';
import { LinkGroup, Link } from 'Styles/links';

import * as Styled from './NavDrawer.styled';
import { SlidingPanelsProvider, SlidingPanel } from '../../../SlidingPanel';
import { vercel_base_url } from '../../../../util/config';
import { BUILD } from '../../../../util/const';
import CountryPickerLink from '../../../CountryPickerLink/CountryPickerLink';

/**
 * NavDrawer renders the Navigation Drawer on smaller screens when the hamburger icon is clicked
 */
const NavDrawer = ({
  isOpen,
  isLoggedIn,
  onRequestClose,
  rootPanel,
  slidingPanels
}) => {
  const base_url = BUILD === 'shopify' ? vercel_base_url : '';
  const LinkComponent = BUILD === 'shopify' ? 'a' : NextLink;

  return (
    <Styled.NavDrawer
      position="left"
      isOpen={isOpen}
      onRequestClose={onRequestClose}
    >
      <SlidingPanelsProvider isOpen={isOpen} onEveryClose={onRequestClose}>
        <SlidingPanel
          panelId="root"
          data-label='navDrawer'
          leftHeaderComponent={
            <Styled.IconLink href={vercel_base_url} aria-label="Bombas">
              <Styled.Icon name="logo-type" size="20" />
            </Styled.IconLink>
          }
        >
          <Styled.PanelItemsContainer>
            {rootPanel
              ?.filter((level1Section) => !level1Section.hideOnMobile)
              .map((panelInfo) => (
                <Styled.PanelItem forPanelId={panelInfo.id} key={panelInfo.id}>
                  {panelInfo.text}
                </Styled.PanelItem>
              ))}
          </Styled.PanelItemsContainer>
          <Styled.Divider />
          <Styled.RootLinksContainer>
            <LinkGroup>
              <Link
                href={`${vercel_base_url}/pages/help-center`}
                aria-label="Static Help Link"
              >
                Help
              </Link>
              <Link
                href={`${vercel_base_url}/pages/find-a-store`}
                aria-label="Static Find A Store Link"
              >
                Find A Store
              </Link>
              {isLoggedIn ? (
                <>
                  <Link
                    as={LinkComponent}
                    href={`${base_url}/account`}
                    onClick={onRequestClose}
                  >
                    My Account
                  </Link>
                  <Link
                    as={LinkComponent}
                    href={`${base_url}/buy-again`}
                    onClick={onRequestClose}
                  >
                    Buy Again
                  </Link>
                </>
              ) : (
                <Link
                  as="a"
                  href={`${vercel_base_url}/account/login`}
                  onClick={onRequestClose}
                >
                  Log In
                </Link>
              )}
              <CountryPickerLink />
            </LinkGroup>
          </Styled.RootLinksContainer>
        </SlidingPanel>
        {slidingPanels}
      </SlidingPanelsProvider>
    </Styled.NavDrawer>
  );
};

NavDrawer.propTypes = {
  /**
   * Parent component maintains the open/close state and passes down
   */
  isOpen: PropTypes.bool,
  /**
   * If true, render My Account and Buy Again link; otherwise, render Log In link
   */
  isLoggedIn: PropTypes.bool,
  /**
   * A function that is called when `NavDrawer` is requesting to close.
   */
  onRequestClose: PropTypes.func.isRequired,
  /**
   * Array of Navigation sections, only Navigation Level1Section will be used to render root panel
   */
  rootPanel: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  /**
   * Completed SlidingPanels to render
   */
  slidingPanels: PropTypes.arrayOf(PropTypes.shape({})).isRequired
};

NavDrawer.defaultProps = {
  isOpen: false,
  isLoggedIn: false
};

export default NavDrawer;
